import { Action } from "redux";

import { FormOpenMethod } from "@multiweb/reactCore/bridge";
import { isArray, Key } from "@multiweb/reactUtils";
import { IMenuNavigationConfig } from "@multiweb/types/multiweb";
import { SerializableKeyEvent } from "@multiweb/types/react";
import { LOC_KEY_QUICK_ACCESS_MENU_FAVORITES, LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS, LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS } from "../locKeys";
import { SearchGroup, SearchResultType } from "../search";
import { IMenuItemActivatedAction, MenuItemActivatedType } from "../types";
import { CompleteNavigationConfig, FocusableItemType, MenuFocusableItemType, MenuNavigationSectionType, MutableNavigationNode, NavigationNode, NavigationSection } from "./types";

/**
 * Vytvoří id pro zaměřitelnou položku z kombinace typu a id položky.
 * @param itemId Id položky.
 * @param type Typ položky.
 * @param section Sekce navigace, do které položka patří.
 */
export const createFocusableItemId = (itemId: string | number, type: FocusableItemType, section: NavigationSection) => `${section}-${type}-${itemId}`;

/**
 * Vytvoří navigační uzel pro položku daného typu a id. Uzel neobsahuje žádné sousedy.
 * @param itemId Id položky. Nemusí být unikátní napříč typy, stačí je v rámci typu.
 * @param type Typ položky.
 * @param section Sekce navigace, do které položka patří.
 */
export const createNavigationNode = (itemId: string | number, type: FocusableItemType, section: NavigationSection): MutableNavigationNode => ({
    self: {
        id: createFocusableItemId(itemId, type, section),
        itemId,
        type,
        section,
    },
});

/**
 * Vytvoří navigační uzly daného typu pro položky se zadanými identifikátory.
 * @param itemIds Identifikátory položek.
 * @param type Typ položek.
 * @param section Sekce navigace, do které položky patří.
 */
export const createMultipleNavigationNodes = (itemIds: ReadonlyArray<string | number>, type: FocusableItemType, section: NavigationSection): MutableNavigationNode[] => (
    itemIds.map((itemId) => createNavigationNode(itemId, type, section))
);

/**
 * Propojí navzájem dva navigační uzly vertikálně. Propojuje předané uzly, nevytváří jejich kopii.
 * @param topNode Horní uzel.
 * @param bottomNode Spodní uzel.
 */
export const connectVertically = (topNode: MutableNavigationNode, bottomNode: MutableNavigationNode) => {
    topNode.downId = bottomNode.self.id;
    bottomNode.upId = topNode.self.id;
};

/**
 * Propojí navzájem dva navigační uzly horizontálně. Propojuje předané uzly, nevytváří jejich kopii.
 * @param leftNode Levý uzel.
 * @param rightNode Pravý uzel.
 */
export const connectHorizontally = (leftNode: MutableNavigationNode, rightNode: MutableNavigationNode) => {
    leftNode.rightId = rightNode.self.id;
    rightNode.leftId = leftNode.self.id;
};

/**
 * Vytvoří mělkou kopii navigačního uzlu. Umožňuje měnit sousední uzly v kopii.
 * @param sourceNode Uzel ke zkopírování.
 */
export const copyNode = (sourceNode: NavigationNode): MutableNavigationNode => ({ ...sourceNode });

/**
 * Propojí vertikálně zadané navigační uzly. Propojení odpovídá pořadí prvků v poli. Propojuje předané uzly, nevytváří jejich kopii.
 * @param navigationNodes Uzly pro vertikální propojení.
 */
export const connectMultipleVertically = (navigationNodes: readonly MutableNavigationNode[]) => {
    for (let index = 0; index < navigationNodes.length - 1; index++) {
        connectVertically(navigationNodes[index], navigationNodes[index + 1]);
    }
};

/**
 * Propojí vertikálně zadané navigační uzly. Propojení odpovídá pořadí prvků v poli. Zároveň propojí první a poslední uzel. Propojuje předané uzly, nevytváří jejich
 * kopii.
 * Pozn. Pokud by byl v _navigationNodes_ jediný uzel, tak napojen (sám se sebou) nebude.
 * @param navigationNodes
 */
export const connectMultipleVerticallyCyclic = (navigationNodes: readonly MutableNavigationNode[]) => {
    connectMultipleVertically(navigationNodes);
    if (navigationNodes.length >= 2) {
        connectVertically(navigationNodes[navigationNodes.length - 1], navigationNodes[0]);
    }
};

/**
 * Propojí horizontálně zadané navigační uzly. Propojení odpovídá pořadí prvků v poli. Propojuje předané uzly, nevytváří jejich kopii.
 * @param navigationNodes Uzly pro horizontální propojení.
 */
export const connectMultipleHorizontally = (navigationNodes: readonly MutableNavigationNode[]) => {
    for (let index = 0; index < navigationNodes.length - 1; index++) {
        connectHorizontally(navigationNodes[index], navigationNodes[index + 1]);
    }
};

/**
 * Propojí jednosměrně všechny zadané navigační uzly vlevo na zadaný levý uzel. Propojuje předané uzly, nevytváří jejich kopii. Levý uzel není modifikován.
 * @param navigationNodes Navigační uzly, kterým se jako levý soused nastaví zadaný leftNode.
 * @param leftNode Cílový uzel, na který se má navigovat.
 */
export const connectMultipleToLeftNode = (navigationNodes: readonly MutableNavigationNode[], leftNode: NavigationNode) => {
    navigationNodes.forEach((navigationNode) => {
        navigationNode.leftId = leftNode.self.id;
    });
};

/**
 * Propojí jednosměrně všechny zadané navigační uzly na zadaný únikový uzel. Propojuje předané uzly, nevytváří jejich kopii. Únikový uzel není modifikován.
 * @param navigationNodes Navigační uzly, kterým se jako únikový uzel nastaví zadaný escNode.
 * @param escNode Cílový uzel, na který se má navigovat v případě stisku klávesy escape.
 */
export const connectMultipleToEsc = (navigationNodes: readonly MutableNavigationNode[], escNode: NavigationNode) => {
    navigationNodes.forEach((navigationNode) => {
        navigationNode.escId = escNode.self.id;
    });
};

/**
 * Získá id následující zaměřené položky na základě aktuální zaměřené položky a stisknuté klávesy.
 * @param keyEvent Událost stisku klávesy.
 * @param focusedNode Aktuálně zaměřený navigační uzel.
 */
export const getNextFocusedItemId = (keyEvent: SerializableKeyEvent, focusedNode: NavigationNode) => {
    switch (keyEvent.key) {
        case Key.ArrowUp: return focusedNode.upId;
        case Key.ArrowDown: return focusedNode.downId;
        case Key.ArrowRight: return focusedNode.rightId;
        case Key.ArrowLeft: return focusedNode.leftId;
        case Key.Escape: return focusedNode.escId;
        default: return undefined;
    }
};

/**
 * Převede metodu otevření formuláře na typ zaměřitelné položky pro akční položky otvírání formulářů.
 * @param openMethod Metoda otevření formuláře.
 */
export const getFocusableTypeByOpenMethod = (openMethod: FormOpenMethod) => {
    switch (openMethod) {
        case FormOpenMethod.inTab: return MenuFocusableItemType.openInTabActionItem;
        case FormOpenMethod.newOrExisting:
        case FormOpenMethod.new:
        default: return MenuFocusableItemType.openActionItem;
    }
};

/**
 * Vytvoří vzor pro rozpoznávání akcí aktivujících položku menu pomocí myši pro zadané typy položek.
 * @param actionTypes Hledaný typ položek. Může být konkrétní typ nebo pole odpovídajících typů.
 */
export const createMenuItemActivatedByMousePattern = (actionTypes: MenuItemActivatedType | readonly MenuItemActivatedType[]) => (action: Action): boolean => {
    if (action instanceof IMenuItemActivatedAction && action.payload.mouseEvent) {
        const { itemType } = action.payload;
        if (isArray(actionTypes)) {
            return actionTypes.includes(itemType);
        } else {
            return itemType === actionTypes;
        }
    } else {
        return false;
    }
};

/**
 * Převede MenuItemActivatedType na MenuFocusableItemType.
 * @param menuItemActivatedType
 */
export const menuItemActivatedTypeToFocusableItemType = (menuItemActivatedType: MenuItemActivatedType): MenuFocusableItemType | undefined => {
    switch (menuItemActivatedType) {
        case MenuItemActivatedType.formFavoriteActionItem:
        case MenuItemActivatedType.openedInstanceFavoriteActionItem:
            return MenuFocusableItemType.favoriteActionItem;
        case MenuItemActivatedType.openFormActionItem:
        case MenuItemActivatedType.openProfileActionItem:
        case MenuItemActivatedType.openFormInstanceActionItem:
            return MenuFocusableItemType.openActionItem;
        case MenuItemActivatedType.openFormInTabActionItem:
        case MenuItemActivatedType.openProfileInTabActionItem:
        case MenuItemActivatedType.openFormInstanceInTabActionItem:
            return MenuFocusableItemType.openInTabActionItem;
        case MenuItemActivatedType.closeActionItem:
            return MenuFocusableItemType.closeActionItem;
        case MenuItemActivatedType.logoutButton:
        case MenuItemActivatedType.settingsButton:
        case MenuItemActivatedType.toggleThemeButton:
            return MenuFocusableItemType.userMenuItem;
        case MenuItemActivatedType.staticPanelItem:
        case MenuItemActivatedType.notificationGroup:
        case MenuItemActivatedType.panelItemForm:
        case MenuItemActivatedType.panelItemFormFolder:
            return MenuFocusableItemType.panelItem;
        case MenuItemActivatedType.form:
            return MenuFocusableItemType.formItem;
        case MenuItemActivatedType.formFolder:
            return MenuFocusableItemType.folderItem;
        case MenuItemActivatedType.profile:
            return MenuFocusableItemType.profile;
        case MenuItemActivatedType.notificationItem:
            return MenuFocusableItemType.notificationItem;
        case MenuItemActivatedType.openedForm:
            return MenuFocusableItemType.openedItem;
        default:
            return undefined;
    }
};

/**
 * Mapuje podřízené hodnoty z MenuNavigationSectionType na svůj rodičovský typ. Hodnoty, které nejsou podřízené vrací nezměněné.
 * @param section
 */
export const collapseMenuNavigationSectionType = (section: NavigationSection) => {
    switch (section) {
        case MenuNavigationSectionType.quickAccessOpened:
        case MenuNavigationSectionType.quickAccessHistoric:
        case MenuNavigationSectionType.quickAccessFavorite:
            return MenuNavigationSectionType.quickAccess;
        case MenuNavigationSectionType.searchResultsOpened:
        case MenuNavigationSectionType.searchResultsHistoric:
        case MenuNavigationSectionType.searchResultsFavorite:
            return MenuNavigationSectionType.searchResults;
        default:
            return section;
    }
};

/**
 * Převede skupinu vyhledávání na sekci navigace.
 * @param searchGroup Skupina vyhledávání pro převod.
 */
export const searchGroupToNavigationSection = (searchGroup: SearchGroup): NavigationSection => {
    switch (searchGroup) {
        case LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS:
            return MenuNavigationSectionType.searchResultsOpened;
        case LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS:
            return MenuNavigationSectionType.searchResultsHistoric;
        case LOC_KEY_QUICK_ACCESS_MENU_FAVORITES:
            return MenuNavigationSectionType.searchResultsFavorite;
        default:
            return searchGroup;
    }
};

/**
 * Převede typ zaměřitelné položky na typ výsledku vyhledávání. Pro položky, které nemají ekvivalent ve výsledcích vyhledávání vrací undefined.
 * @param focusableItemType Typ zaměřitelné položky pro převedení.
 */
export const focusableItemTypeToSearchResultType = (focusableItemType: FocusableItemType): SearchResultType | undefined => {
    switch (focusableItemType) {
        case MenuFocusableItemType.formItem: return SearchResultType.form;
        case MenuFocusableItemType.profile: return SearchResultType.profile;
        case MenuFocusableItemType.openedItem: return SearchResultType.formInstance;
        default:
            return undefined;
    }
};

/**
 * Získá výsledné nastavení navigace složenou z požadovaného nastavení a výchozího nastavení.
 * @param config
 * @param defaultConfig Výchozí konfigurace, která se použije pro vlastnosti, které nejsou specifikované v parametru <code>config</code>.
 */
export const mergeNavigationConfig = (config: IMenuNavigationConfig | undefined, defaultConfig: CompleteNavigationConfig): CompleteNavigationConfig => {
    if (!config) {
        return defaultConfig;
    }

    return {
        enableNavigation: config.enableNavigation,
        optionalFeatures: {
            ...defaultConfig.optionalFeatures,
            ...config.optionalFeatures,
        },
        nodeDefinitions: {
            ...defaultConfig.nodeDefinitions,
            ...config.nodeDefinitions,
        },
    };
};
